import { Button } from "reactstrap";
import PaxStatsModal from "./PaxStatsModal";
import useModal from "../../../../customHooks/useModal";
import useSWR from "swr";
import { customFetcher, getTsDate, notify, useStaticData } from "../../../../utils";
import apiClient from "../../../../apiClient";
import { useState } from "react";
import { PassengerStat } from "./validation";
import useUser from "../../../../customHooks/useUser";
import VirtualTable from "../../../custom/VirtualTable";
import useAirportCodes from "../../../../customHooks/useAirportCodes";
import { AircraftType, DelayCode, SelectOptions } from "../../../../globals/interfaces";
import moment from "moment";

export default function PaxStats() {

    const { user } = useUser();
    const { s_email, s_unit } = user;
    const { airportCodes, airportCodesMap } = useAirportCodes();
    const { data, mutate } = useSWR(`/pax-stats/${s_unit}`, customFetcher<PassengerStat>, {
        fallbackData: []
    });

    const { data: aircraftTypes } = useStaticData<Array<AircraftType>>('/aircraft-type', {
        fallbackData: []
    });

    const { data: delayCodes } = useStaticData<Array<DelayCode>>('/delay-code', {
        fallbackData: []
    });

    const aircraftTypeOptions: SelectOptions = aircraftTypes.map(item => ({ label: item.FAA_Designator, value: item.FAA_Designator }));
    const delayCodeOptions: SelectOptions = delayCodes.map(item => ({ label: `${item.number} - ${item.code}`, value: item.code })).sort((a, b) => +a.value - +b.value);

    const [id, setId] = useState<number>();
    const record = data.find(item => item.id === id);

    const createUpdateStat = async (data: PassengerStat | PassengerStat[], update?: boolean) => {
        try {
            // Helper function to format a single stat record
            const formatStat = (stat: PassengerStat): PassengerStat => ({
                ...stat
            });

            if (Array.isArray(data)) {
                // Handle dual form submission (always create mode)
                const formattedStats = data.map(formatStat);

                // Submit both records in parallel for better performance
                await Promise.all(
                    formattedStats.map(stat =>
                        apiClient.post('/pax-stats', stat)
                    )
                );

                mutate();
                notify('Success - Both flights saved');
            } else {
                // Handle single form submission (create or update)
                const formattedStat = formatStat(data);

                const method = update ? 'put' : 'post';
                await apiClient[method]('/pax-stats', formattedStat);

                mutate();
                notify('Success');
            }
        } catch (err) {
            alert(err);
        }
    }

    const validateStat = async () => {
        await createUpdateStat({
            id: record.id,
            status: 'VALIDATED',
            validated: getTsDate(),
            validated_by: s_email,
            modified: getTsDate(),
            modified_by: s_email
        }, true);
    }

    const deleteStat = async (id: number) => {
        try {
            await apiClient.delete(`/pax-stats/${id}`);
            setModal(false);
            notify('Deleted');
            mutate();
        } catch (err) {
            alert(err);
        }
    }

    const { modal, setModal } = useModal();

    const handleLaunchModal = (id?: number) => {
        if (id) {
            setId(id);
        }
        setModal(true);
    }

    return (
        <div>
            <Button onClick={handleLaunchModal}>New</Button>
            <VirtualTable<PassengerStat>
                data={data}
                mapping={[{
                    name: 'Status',
                    value: 'status'
                }, {
                    name: 'Type',
                    value: 'type'
                }, {
                    name: 'Date',
                    value: 'actual_time',
                    date: true,
                    utc: true
                }, {
                    name: 'Number',
                    value: 'number'
                }, {
                    name: 'Registration',
                    value: 'registration'
                }, {
                    name: 'Model',
                    value: 'model'
                }, {
                    name: 'ORI',
                    value: 'origin',
                    smallWidth: true
                }, {
                    name: 'DES',
                    value: 'destination',
                    smallWidth: true
                }]}
                handleClick={item => handleLaunchModal(item.id)}
            />
            <PaxStatsModal
                modal={modal}
                setModal={setModal}
                onSubmit={createUpdateStat}
                validateStat={validateStat}
                deleteStat={deleteStat}
                record={record}
                airportCodes={airportCodes}
                airportCodesMap={airportCodesMap}
                aircraftTypeOptions={aircraftTypeOptions}
                delayCodeOptions={delayCodeOptions}
            />
        </div>
    );
}